import { useState, useMemo } from "react";
import { Info, Users, Share2, Check } from "lucide-react";
import { CLASS_META, DAYS, hasAvailability, isBackToBackDay } from "./timetable/data";
import { useTimetableData } from "./timetable/useTimetableData";
import { useTimetableFilters } from "./timetable/useTimetableFilters";
import { FilterChip, DayChip } from "./timetable/Chips";
import { DayColumn, DayListCard, OverlapSummary, LEGEND, liveStatus } from "./timetable/DayViews";

const buildShareUrl = (selected, selectedDays) => {
  const params = new URLSearchParams();
  if (selected.length) params.set("classes", selected.join(","));
  if (selectedDays.length) params.set("days", selectedDays.join(","));
  const qs = params.toString();
  return `${window.location.origin}/timetable${qs ? `?${qs}` : ""}`;
};

const shareLabel = (copied, failed) => {
  if (copied) return "Link copied!";
  if (failed) return "Couldn't copy — try again";
  return "Share";
};

export default function Timetable() {
  const { isLoading, isLive, bookingUrl, allSessions, chipClasses } = useTimetableData();
  const {
    selected, selectedDays, availableOnly,
    setAvailableOnly, toggle, clearAll, toggleDay, clearDays,
  } = useTimetableFilters();
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState(false);

  const flash = (setter) => {
    setter(true);
    setTimeout(() => setter(false), 2500);
  };

  const share = async () => {
    const url = buildShareUrl(selected, selectedDays);
    const text = selected.length ? `Happy Tiddlers swim classes: ${selected.join(", ")}` : "Happy Tiddlers class timetable";
    try {
      if (navigator.share) {
        await navigator.share({ title: "Happy Tiddlers timetable", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        flash(setCopied);
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user dismissed the share dialog
      console.error("Share failed", err);
      flash(setShareError);
    }
  };

  const visibleDays = useMemo(
    () => (selectedDays.length ? DAYS.filter((d) => selectedDays.includes(d)) : DAYS),
    [selectedDays]
  );
  const multi = selected.length >= 2;

  const byDay = useMemo(() => {
    const filtered = allSessions.filter(
      (s) => (selected.length === 0 || selected.includes(s.cls)) && (!availableOnly || hasAvailability(s))
    );
    return visibleDays.map((day) => {
      const sessions = filtered.filter((s) => s.day === day).sort((a, b) => a.minute - b.minute);
      return { day, sessions, overlap: multi && isBackToBackDay(sessions, selected) };
    });
  }, [allSessions, selected, visibleDays, availableOnly, multi]);

  const overlapDays = byDay.filter((d) => d.overlap).map((d) => d.day);
  const status = liveStatus(isLoading, isLive);
  const hasFilters = selected.length > 0 || selectedDays.length > 0;

  return (
    <section id="timetable" className="py-24 md:py-32 bg-white" data-testid="timetable-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-10">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Weekly Planner</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
            Your week at Happy Tiddlers
          </h2>
          <p className="mt-5 text-lg text-[#334155]">
            Browse the full class timetable across the week with <span className="font-semibold text-[#0F172A]">live availability</span>. Select one or more classes to compare times — handy when you're finding spaces for more than one child.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 mb-3" data-testid="timetable-filters">
          <FilterChip label="All classes" isActive={selected.length === 0} onClick={clearAll} color="#0F172A" value="All" />
          {chipClasses.map((name) => (
            <FilterChip key={name} label={name} isActive={selected.includes(name)} onClick={() => toggle(name)} color={CLASS_META[name] || "#64748B"} value={name} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="timetable-day-filters">
          <span className="text-xs font-bold uppercase tracking-wider text-[#94A3B8] mr-1">Day</span>
          <DayChip label="All week" isActive={selectedDays.length === 0} onClick={clearDays} value="All" />
          {DAYS.map((day) => (
            <DayChip key={day} label={day.slice(0, 3)} isActive={selectedDays.includes(day)} onClick={() => toggleDay(day)} value={day} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="availability-toggle-row">
          <button
            onClick={() => setAvailableOnly((v) => !v)}
            className={`inline-flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 text-sm font-semibold border transition-all duration-200 ${
              availableOnly ? "bg-[#10B981] text-white border-transparent shadow-sm" : "bg-white text-[#334155] border-slate-200 hover:border-slate-300"
            }`}
            data-testid="available-toggle"
            aria-pressed={availableOnly}
          >
            <span className={`grid place-items-center w-5 h-5 rounded-full ${availableOnly ? "bg-white/25" : "bg-slate-100"}`}>
              {availableOnly && <Check size={13} strokeWidth={3} />}
            </span>
            Show only classes with spaces
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs" data-testid="timetable-legend">
          <span className="inline-flex items-center gap-1.5 font-semibold" data-testid="timetable-live-status">
            <span className={`w-2 h-2 rounded-full ${status.dot}`} />
            <span className={isLive ? "text-[#0E9F6E]" : "text-[#64748B]"}>{status.label}</span>
          </span>
          {LEGEND.map(([dot, label]) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-[#64748B]"><span className={`w-2.5 h-2.5 rounded-full ${dot}`} /> {label}</span>
          ))}
        </div>

        <div className="mb-4 h-5 text-sm text-[#64748B]" data-testid="filter-status">
          {hasFilters && (
            <span>
              Showing
              {selected.length > 0 && ` ${selected.length} class${selected.length > 1 ? "es" : ""}`}
              {selected.length > 0 && selectedDays.length > 0 && " ·"}
              {selectedDays.length > 0 && ` ${selectedDays.length} day${selectedDays.length > 1 ? "s" : ""}`}
              .{" "}
              <button onClick={() => { clearAll(); clearDays(); }} className="font-semibold text-[#FF7A1A] hover:underline" data-testid="clear-filters">
                Clear all
              </button>
            </span>
          )}
        </div>

        {multi && <OverlapSummary selectedCount={selected.length} overlapDays={overlapDays} />}

        <div className="hidden lg:block overflow-x-auto pb-4" data-testid="week-planner">
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${visibleDays.length}, minmax(0, 1fr))`, minWidth: visibleDays.length >= 5 ? 980 : undefined }}
          >
            {byDay.map((d) => <DayColumn key={d.day} {...d} bookingUrl={bookingUrl} />)}
          </div>
        </div>

        <div className="lg:hidden space-y-4" data-testid="week-planner-mobile">
          {byDay.every((d) => d.sessions.length === 0) && (
            <div className="rounded-2xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="no-classes-mobile">
              No classes match your filters.
            </div>
          )}
          {byDay.filter((d) => d.sessions.length > 0).map((d) => <DayListCard key={d.day} {...d} bookingUrl={bookingUrl} />)}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="space-y-1.5">
            <p className="flex items-center gap-2 text-sm text-[#64748B]">
              <Info size={15} /> Class times and availability vary — please book to confirm your space.
            </p>
            <p className="flex items-center gap-2 text-sm font-semibold text-[#10B981]">
              <Users size={15} /> Siblings welcome — ask us about family discounts when you enrol more than one little swimmer.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={share}
              className="inline-flex items-center gap-2 bg-white text-[#0A2540] border-2 border-[#0A2540] font-bold px-6 py-3 rounded-full hover:bg-[#0A2540] hover:text-white transition-all duration-300"
              data-testid="timetable-share"
            >
              <Share2 size={16} /> {shareLabel(copied, shareError)}
            </button>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FF7A1A] text-white font-bold px-7 py-3.5 rounded-full shadow-[0_8px_20px_-6px_rgba(255,122,26,0.6)] hover:-translate-y-0.5 transition-all duration-300"
              data-testid="timetable-book-cta"
            >
              Book your class
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
